import omit from 'lodash/omit';

export type TableCell = {
  key: string;
  type?: string;
  value?: any;
  colSpan?: number;
  rowSpan?: number;
  merged?: boolean;
  [key: string]: unknown;
};

export type TableRow = {
  key: string;
  cells: TableCell[];
  [key: string]: unknown;
};

export type MergeDirection = 'right' | 'down';

export type CellPosition = { row: number; cell: number };

const getId = () => Math.floor(Math.random() * Math.pow(2, 24)).toString(32);

const emptyParagraph = () => [{ type: 'p', children: [{ text: '' }] }];

export const emptyCell = (type = 'data'): TableCell => ({
  key: getId(),
  type,
  value: emptyParagraph(),
});

const colSpanOf = (cell: TableCell) => cell.colSpan || 1;
const rowSpanOf = (cell: TableCell) => cell.rowSpan || 1;

const withSpans = (
  cell: TableCell,
  colSpan: number,
  rowSpan: number,
): TableCell => {
  const next = omit(cell, ['colSpan', 'rowSpan']) as TableCell;
  if (colSpan > 1) next.colSpan = colSpan;
  if (rowSpan > 1) next.rowSpan = rowSpan;
  return next;
};

const coveredCell = (cell: TableCell): TableCell => ({
  key: cell.key,
  type: cell.type,
  value: emptyParagraph(),
  merged: true,
});

const releasedCell = (cell: TableCell): TableCell => ({
  key: cell.key,
  type: cell.type,
  value: emptyParagraph(),
});

const isEmptyBlock = (node: any) =>
  Array.isArray(node?.children) &&
  node.children.every(
    (child: any) => typeof child?.text === 'string' && child.text === '',
  );

const joinValues = (first: any, second: any) => {
  const firstNodes = Array.isArray(first) ? first : [];
  const secondNodes = Array.isArray(second) ? second : [];
  const filled = [...firstNodes, ...secondNodes].filter(
    (node) => !isEmptyBlock(node),
  );
  if (filled.length > 0) return filled;
  return firstNodes.length > 0 ? firstNodes : emptyParagraph();
};

const clipHeaderRowSpans = (rows: TableRow[]): TableRow[] => {
  const headerCells = rows[0]?.cells;
  if (!headerCells?.some((cell) => rowSpanOf(cell) > 1)) return rows;

  const released = new Set<string>();
  headerCells.forEach((cell, cellIndex) => {
    if (cell.merged || rowSpanOf(cell) <= 1) return;
    for (let r = 1; r < rowSpanOf(cell); r++) {
      for (let c = cellIndex; c < cellIndex + colSpanOf(cell); c++) {
        released.add(`${r}:${c}`);
      }
    }
  });

  return rows.map((row, rowIndex) => ({
    ...row,
    cells: row.cells.map((cell, cellIndex) => {
      if (rowIndex === 0) {
        return cell.merged ? cell : withSpans(cell, colSpanOf(cell), 1);
      }
      return released.has(`${rowIndex}:${cellIndex}`)
        ? releasedCell(cell)
        : cell;
    }),
  }));
};

export const hasMergedCells = (rows: TableRow[] | undefined): boolean =>
  (rows || []).some((row) =>
    (row.cells || []).some(
      (cell) => cell.merged || colSpanOf(cell) > 1 || rowSpanOf(cell) > 1,
    ),
  );

export const findOrigin = (
  rows: TableRow[],
  row: number,
  cell: number,
): CellPosition => {
  if (!rows[row]?.cells[cell]?.merged) return { row, cell };
  for (let r = row; r >= 0; r--) {
    for (let c = cell; c >= 0; c--) {
      const candidate = rows[r]?.cells[c];
      if (
        candidate &&
        !candidate.merged &&
        r + rowSpanOf(candidate) > row &&
        c + colSpanOf(candidate) > cell
      ) {
        return { row: r, cell: c };
      }
    }
  }
  return { row, cell };
};

export const canMerge = (
  rows: TableRow[] | undefined,
  row: number,
  cell: number,
  direction: MergeDirection,
): boolean => {
  const origin = rows?.[row]?.cells?.[cell];
  if (!rows || !origin || origin.merged) return false;

  if (direction === 'right') {
    const target = rows[row].cells[cell + colSpanOf(origin)];
    return (
      !!target && !target.merged && rowSpanOf(target) === rowSpanOf(origin)
    );
  }

  if (row === 0) return false;
  const target = rows[row + rowSpanOf(origin)]?.cells[cell];
  return !!target && !target.merged && colSpanOf(target) === colSpanOf(origin);
};

export const canUnmerge = (
  rows: TableRow[] | undefined,
  row: number,
  cell: number,
): boolean => {
  const origin = rows?.[row]?.cells?.[cell];
  return !!origin && (colSpanOf(origin) > 1 || rowSpanOf(origin) > 1);
};

export const mergeCells = (
  rows: TableRow[],
  row: number,
  cell: number,
  direction: MergeDirection,
): TableRow[] => {
  if (!canMerge(rows, row, cell, direction)) return rows;

  const origin = rows[row].cells[cell];
  const isRight = direction === 'right';
  const targetRow = isRight ? row : row + rowSpanOf(origin);
  const targetCell = isRight ? cell + colSpanOf(origin) : cell;
  const target = rows[targetRow].cells[targetCell];

  const merged: TableCell = {
    ...withSpans(
      origin,
      isRight ? colSpanOf(origin) + colSpanOf(target) : colSpanOf(origin),
      isRight ? rowSpanOf(origin) : rowSpanOf(origin) + rowSpanOf(target),
    ),
    key: getId(),
    value: joinValues(origin.value, target.value),
  };

  return rows.map((currentRow, rowIndex) => ({
    ...currentRow,
    cells: currentRow.cells.map((currentCell, cellIndex) => {
      if (rowIndex === row && cellIndex === cell) return merged;
      if (rowIndex === targetRow && cellIndex === targetCell) {
        return coveredCell(currentCell);
      }
      return currentCell;
    }),
  }));
};

export const unmergeCells = (
  rows: TableRow[],
  row: number,
  cell: number,
): TableRow[] => {
  if (!canUnmerge(rows, row, cell)) return rows;

  const origin = rows[row].cells[cell];
  const lastRow = row + rowSpanOf(origin);
  const lastCell = cell + colSpanOf(origin);

  return rows.map((currentRow, rowIndex) => ({
    ...currentRow,
    cells: currentRow.cells.map((currentCell, cellIndex) => {
      if (rowIndex === row && cellIndex === cell) {
        return { ...withSpans(origin, 1, 1), key: getId() };
      }
      const isCovered =
        rowIndex >= row &&
        rowIndex < lastRow &&
        cellIndex >= cell &&
        cellIndex < lastCell;
      return isCovered ? releasedCell(currentCell) : currentCell;
    }),
  }));
};

export const insertRow = (rows: TableRow[], at: number): TableRow[] => {
  const coveredColumns = new Set<number>();
  const grown = rows.map((row, rowIndex) => ({
    ...row,
    cells: row.cells.map((cell, cellIndex) => {
      const crosses =
        !cell.merged && rowIndex < at && at < rowIndex + rowSpanOf(cell);
      if (!crosses) return cell;
      for (let c = cellIndex; c < cellIndex + colSpanOf(cell); c++) {
        coveredColumns.add(c);
      }
      return withSpans(cell, colSpanOf(cell), rowSpanOf(cell) + 1);
    }),
  }));

  const newRow: TableRow = {
    key: getId(),
    cells: (rows[0]?.cells || []).map((_, cellIndex) => {
      const cell = emptyCell();
      return coveredColumns.has(cellIndex) ? coveredCell(cell) : cell;
    }),
  };

  return [...grown.slice(0, at), newRow, ...grown.slice(at)];
};

export const insertColumn = (
  rows: TableRow[],
  at: number,
  typeColumn: number,
): TableRow[] => {
  const coveredRows = new Set<number>();
  const grown = rows.map((row, rowIndex) => ({
    ...row,
    cells: row.cells.map((cell, cellIndex) => {
      const crosses =
        !cell.merged && cellIndex < at && at < cellIndex + colSpanOf(cell);
      if (!crosses) return cell;
      for (let r = rowIndex; r < rowIndex + rowSpanOf(cell); r++) {
        coveredRows.add(r);
      }
      return withSpans(cell, colSpanOf(cell) + 1, rowSpanOf(cell));
    }),
  }));

  return grown.map((row, rowIndex) => {
    const cell = emptyCell(rows[rowIndex].cells[typeColumn]?.type || 'data');
    return {
      ...row,
      cells: [
        ...row.cells.slice(0, at),
        coveredRows.has(rowIndex) ? coveredCell(cell) : cell,
        ...row.cells.slice(at),
      ],
    };
  });
};

export const deleteRow = (rows: TableRow[], at: number): TableRow[] => {
  const next = rows.map((row) => ({ ...row, cells: [...row.cells] }));

  rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell, cellIndex) => {
      if (cell.merged || rowSpanOf(cell) <= 1) return;
      if (rowIndex < at && at < rowIndex + rowSpanOf(cell)) {
        next[rowIndex].cells[cellIndex] = withSpans(
          cell,
          colSpanOf(cell),
          rowSpanOf(cell) - 1,
        );
      } else if (rowIndex === at) {
        const heir = rows[at + 1].cells[cellIndex];
        next[at + 1].cells[cellIndex] = {
          ...withSpans(
            omit(heir, 'merged') as TableCell,
            colSpanOf(cell),
            rowSpanOf(cell) - 1,
          ),
          value: cell.value,
        };
      }
    });
  });

  return clipHeaderRowSpans(next.filter((_, rowIndex) => rowIndex !== at));
};

export const deleteColumn = (rows: TableRow[], at: number): TableRow[] => {
  const next = rows.map((row) => ({ ...row, cells: [...row.cells] }));

  rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell, cellIndex) => {
      if (cell.merged || colSpanOf(cell) <= 1) return;
      if (cellIndex < at && at < cellIndex + colSpanOf(cell)) {
        next[rowIndex].cells[cellIndex] = withSpans(
          cell,
          colSpanOf(cell) - 1,
          rowSpanOf(cell),
        );
      } else if (cellIndex === at) {
        const heir = row.cells[at + 1];
        next[rowIndex].cells[at + 1] = {
          ...withSpans(
            omit(heir, 'merged') as TableCell,
            colSpanOf(cell) - 1,
            rowSpanOf(cell),
          ),
          value: cell.value,
        };
      }
    });
  });

  return next.map((row) => ({
    ...row,
    cells: row.cells.filter((_, cellIndex) => cellIndex !== at),
  }));
};

export const expandPastedSpans = (rows: TableRow[]): TableRow[] => {
  if (!hasMergedCells(rows)) return rows;

  const grid: TableCell[][] = rows.map(() => []);

  rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell) => {
      let column = 0;
      while (grid[rowIndex][column]) column++;

      let colSpan = 1;
      while (colSpan < colSpanOf(cell) && !grid[rowIndex][column + colSpan]) {
        colSpan++;
      }

      let rowSpan = 1;
      const fitsBelow = (r: number) => {
        for (let c = column; c < column + colSpan; c++) {
          if (grid[r][c]) return false;
        }
        return true;
      };
      while (
        rowSpan < rowSpanOf(cell) &&
        rowIndex + rowSpan < rows.length &&
        fitsBelow(rowIndex + rowSpan)
      ) {
        rowSpan++;
      }

      for (let r = rowIndex; r < rowIndex + rowSpan; r++) {
        for (let c = column; c < column + colSpan; c++) {
          grid[r][c] = coveredCell(emptyCell(cell.type));
        }
      }
      grid[rowIndex][column] = withSpans(cell, colSpan, rowSpan);
    });
  });

  const width = Math.max(...grid.map((cells) => cells.length));

  return clipHeaderRowSpans(
    rows.map((row, rowIndex) => ({
      ...row,
      cells: Array.from(
        { length: width },
        (_, column) => grid[rowIndex][column] || emptyCell(),
      ),
    })),
  );
};
